import React, { useEffect, useState, useCallback } from 'react';
import Layout from './Layout';
import './css/SpecialReply.css';

const SpecialReplyPage = () => {
  const [user, setUser] = useState(null);
  
  // Main data structure - 3 levels
  const [groups, setGroups] = useState([]);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dragError, setDragError] = useState(''); 
  
  // Expanded state for accordion - 3 levels
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [expandedCategories, setExpandedCategories] = useState({});
  
  // Drag and drop states
  const [draggedItem, setDraggedItem] = useState(null);
  const [draggedCategoryId, setDraggedCategoryId] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isSequenceUpdating, setIsSequenceUpdating] = useState(false);
  const [preventFormSubmission, setPreventFormSubmission] = useState(false);

  // Modal states
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showEditGroupModal, setShowEditGroupModal] = useState(false);
  const [showDeleteGroupModal, setShowDeleteGroupModal] = useState(false);
  
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showEditCategoryModal, setShowEditCategoryModal] = useState(false);
  const [showDeleteCategoryModal, setShowDeleteCategoryModal] = useState(false);
  
  const [showTextModal, setShowTextModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showAudioModal, setShowAudioModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  // Edit/Delete states
  const [editingGroup, setEditingGroup] = useState(null);
  const [deletingGroup, setDeletingGroup] = useState(null);
  
  const [editingCategory, setEditingCategory] = useState(null);
  const [deletingCategory, setDeletingCategory] = useState(null);
  
  const [editingItem, setEditingItem] = useState(null);
  const [deletingItem, setDeletingItem] = useState(null);
  
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  
  // Form states
  const [groupForm, setGroupForm] = useState({
    groupName: '',
    status: 1
  });
  
  const [editGroupForm, setEditGroupForm] = useState({
    groupName: '',
    status: 1
  });
  
  const [categoryForm, setCategoryForm] = useState({
    keywords: '',
    groupId: null,
    cooldownHours: 24,
    status: 1
  });
  
  const [editCategoryForm, setEditCategoryForm] = useState({
    keywords: '',
    groupId: null,
    cooldownHours: 24,
    status: 1
  });
  
  const [textForm, setTextForm] = useState({
    textContent: '',
    categoryId: null
  });
  
  const [imageForm, setImageForm] = useState({
    categoryId: null,
    image: null
  });
  
  const [videoForm, setVideoForm] = useState({
    categoryId: null,
    video: null
  });

  const [audioForm, setAudioForm] = useState({
    categoryId: null,
    audio: null
  });
  
  const [editForm, setEditForm] = useState({
    textContent: '',
    mediaName: '',
    categoryId: null,
    image: null,
    video: null,
    audio: null
  });

  // Preview states
  const [imagePreview, setImagePreview] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [audioPreview, setAudioPreview] = useState(null);
  const [editImagePreview, setEditImagePreview] = useState(null);
  const [editVideoPreview, setEditVideoPreview] = useState(null);
  const [editAudioPreview, setEditAudioPreview] = useState(null);
  const [videoLoadingStates, setVideoLoadingStates] = useState({});
  
  // Helper functions
  const getTypeLabel = (type) => {
    switch(type) {
      case 1: return 'Text';
      case 2: return 'Image';
      case 3: return 'Video';
      case 4: return 'Audio';
      default: return 'Unknown';
    }
  };
  
  const getGitHubUrl = (filename) => {
    if (!filename) return null;
    return `/api/specialReply/media/${filename}`;
  };

  const getCurrentMediaUrl = (item) => {
    if (item.TYPE === 2 || item.TYPE === 3 || item.TYPE === 4) {
      if (item.MEDIANAME) {
        return getGitHubUrl(item.MEDIANAME);
      }
    }
    return null;
  };

  const handleMediaError = async (e, filename) => {
    console.error('Media load error for file:', filename);
    
    const isVideo = e.target.tagName === 'VIDEO';
    e.target.style.display = 'none';
    
    if (e.target.parentNode.querySelector('.media-error-container')) {
      return;
    }
    
    const errorDiv = document.createElement('div');
    errorDiv.className = 'media-error-container';
    errorDiv.style.cssText = `
      padding: 15px; 
      text-align: center; 
      color: #666; 
      border: 2px dashed #dc3545; 
      border-radius: 8px; 
      background: linear-gradient(135deg, #fff5f5, #fed7d7);
      margin: 10px 0;
      min-height: ${isVideo ? '150px' : '100px'};
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
    `;
    
    errorDiv.innerHTML = `
      <div style="font-size: 24px; margin-bottom: 8px; color: #dc3545;">
        ${isVideo ? '🎥' : '⚠️'}
      </div>
      <div style="font-weight: bold; margin-bottom: 6px; color: #721c24;">
        ${isVideo ? 'Video' : 'Image'} Loading Failed
      </div>
      <div style="font-size: 12px; color: #975a16; margin-bottom: 8px;">${filename}</div>
    `;
    
    e.target.parentNode.appendChild(errorDiv);
  };
  
  // Load all groups with nested categories and items
  const loadGroups = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await fetch('/api/specialReply/groups');
      const data = await response.json();

      if (data.success) {
        setGroups(Array.isArray(data.data) ? data.data : []);
      } else {
        setError(data.message || 'Failed to load groups');
        setGroups([]);
      }
    } catch (err) {
      setError('Error loading groups: ' + err.message);
      setGroups([]);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    loadGroups();
  }, []);

  // ========================================
  // GROUP MANAGEMENT (Level 1)
  // ========================================
  
  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };
  
  const handleAddGroup = () => {
    setGroupForm({ groupName: '', status: 1 });
    setShowGroupModal(true);
  };
  
  const handleGroupInputChange = (e) => {
    const { name, value } = e.target;
    setGroupForm(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleGroupSubmit = async (e) => {
    e.preventDefault();
    
    if (!groupForm.groupName.trim()) {
      setError('Group name is required');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      const response = await fetch('/api/specialReply/groups', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(groupForm)
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess('Group created successfully');
        loadGroups();
        setTimeout(() => {
          setShowGroupModal(false);
          setGroupForm({ groupName: '', status: 1 });
          setSuccess('');
        }, 700);
      } else {
        setError(data.message || 'Failed to create group');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleEditGroup = (group) => {
    setEditingGroup(group);
    setEditGroupForm({
      groupName: group.GROUP_NAME,
      status: group.STATUS
    });
    setShowEditGroupModal(true);
  };
  
  const handleEditGroupInputChange = (e) => {
    const { name, value } = e.target;
    setEditGroupForm(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleEditGroupSubmit = async (e) => {
    e.preventDefault();
    
    if (!editingGroup) return;
    
    try {
      setLoading(true);
      setError('');
      
      const response = await fetch(`/api/specialReply/groups/${editingGroup.PKKEY}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editGroupForm)
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess('Group updated successfully');
        loadGroups();
        setTimeout(() => {
          setShowEditGroupModal(false);
          setEditingGroup(null);
          setSuccess('');
        }, 700);
      } else {
        setError(data.message || 'Failed to update group');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteGroup = (group) => {
    setDeletingGroup(group);
    setShowDeleteGroupModal(true);
  };
  
  const confirmDeleteGroup = async () => {
    if (!deletingGroup) return;
    
    try {
      setLoading(true);
      setError('');
      
      const response = await fetch(`/api/specialReply/groups/${deletingGroup.PKKEY}`, {
        method: 'DELETE'
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess('Group deleted successfully');
        loadGroups();
        setTimeout(() => {
          setShowDeleteGroupModal(false);
          setDeletingGroup(null);
          setSuccess('');
        }, 700);
      } else {
        setError(data.message || 'Failed to delete group');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // ========================================
  // CATEGORY MANAGEMENT (Level 2)
  // ========================================
  
  const toggleCategory = (groupId, categoryId) => {
    setExpandedCategories(prev => {
      const groupCategories = prev[groupId] || new Set();
      const newGroupCategories = new Set(groupCategories);
      
      if (newGroupCategories.has(categoryId)) {
        newGroupCategories.delete(categoryId);
      } else {
        newGroupCategories.add(categoryId);
      }
      
      return {
        ...prev,
        [groupId]: newGroupCategories
      };
    });
  };
  
  const handleAddCategory = (groupId = null) => {
    setCategoryForm({
      keywords: '',
      groupId: groupId,
      cooldownHours: 24,
      status: 1
    });
    setSelectedGroupId(groupId);
    setShowCategoryModal(true);
  };
  
  const handleCategoryInputChange = (e) => {
    const { name, value } = e.target;
    setCategoryForm(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    
    if (!categoryForm.keywords.trim()) {
      setError('Keywords are required');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      const response = await fetch('/api/specialReply/categories', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(categoryForm)
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess('Category created successfully');
        loadGroups();
        setTimeout(() => {
          setShowCategoryModal(false);
          setCategoryForm({
            keywords: '',
            groupId: null,
            cooldownHours: 24,
            status: 1
          });
          setSuccess('');
        }, 700);
      } else {
        setError(data.message || 'Failed to create category');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setEditCategoryForm({
      keywords: category.KEYWORDS,
      groupId: category.GROUP_ID,
      cooldownHours: category.COOLDOWN_HOURS || 24,
      status: category.STATUS
    });
    setShowEditCategoryModal(true);
  };
  
  const handleEditCategoryInputChange = (e) => {
    const { name, value } = e.target;
    setEditCategoryForm(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleEditCategorySubmit = async (e) => {
    e.preventDefault();
    
    if (!editingCategory) return;
    
    try {
      setLoading(true);
      setError('');
      
      const response = await fetch(`/api/specialReply/categories/${editingCategory.PKKEY}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editCategoryForm)
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess('Category updated successfully');
        loadGroups();
        setTimeout(() => {
          setShowEditCategoryModal(false);
          setEditingCategory(null);
          setSuccess('');
        }, 700);
      } else {
        setError(data.message || 'Failed to update category');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteCategory = (category) => {
    setDeletingCategory(category);
    setShowDeleteCategoryModal(true);
  };
  
  const confirmDeleteCategory = async () => {
    if (!deletingCategory) return;
    
    try {
      setLoading(true);
      setError('');
      
      const response = await fetch(`/api/specialReply/categories/${deletingCategory.PKKEY}`, {
        method: 'DELETE'
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess('Category deleted successfully');
        loadGroups();
        setTimeout(() => {
          setShowDeleteCategoryModal(false);
          setDeletingCategory(null);
          setSuccess('');
        }, 700);
      } else {
        setError(data.message || 'Failed to delete category');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // ========================================
  // ITEM MANAGEMENT (Level 3)
  // ========================================
  
  const handleAddText = (categoryId) => {
    setTextForm({ textContent: '', categoryId });
    setSelectedCategoryId(categoryId);
    setShowTextModal(true);
  };
  
  const handleAddImage = (categoryId) => {
    setImageForm({ categoryId, image: null });
    setSelectedCategoryId(categoryId);
    setImagePreview(null);
    setShowImageModal(true);
  };
  
  const handleAddVideo = (categoryId) => {
    setVideoForm({ categoryId, video: null });
    setSelectedCategoryId(categoryId);
    setVideoPreview(null);
    setShowVideoModal(true);
  };

  const handleAddAudio = (categoryId) => {
    setAudioForm({ categoryId, audio: null });
    setSelectedCategoryId(categoryId);
    setAudioPreview(null);
    setShowAudioModal(true);
  };
  
  const validateImageFile = (file) => {
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    const maxSize = 50 * 1024 * 1024;
    
    if (!allowedTypes.includes(file.type)) {
      return 'Invalid file type. Only JPG, JPEG, PNG, and GIF images are allowed.';
    }
    
    if (file.size > maxSize) {
      return 'File size too large. Maximum size is 50MB.';
    }
    
    return null;
  };

  const validateVideoFile = (file) => {
    const allowedTypes = ['video/mp4'];
    const maxSize = 50 * 1024 * 1024;
    
    if (!allowedTypes.includes(file.type)) {
      return 'Invalid file type. Only MP4 videos are allowed.';
    }
    
    if (file.size > maxSize) {
      return 'File size too large. Maximum size is 50MB.';
    }
    
    return null;
  };

  const validateAudioFile = (file) => {
    const allowedTypes = ['audio/mpeg', 'audio/mp3', 'audio/ogg', 'audio/wav', 'audio/aac', 'audio/x-m4a'];
    const maxSize = 50 * 1024 * 1024;

    if (!allowedTypes.includes(file.type)) {
      return 'Invalid file type. Only MP3, OGG, WAV, AAC, and M4A audio files are allowed.';
    }

    if (file.size > maxSize) {
      return 'File size too large. Maximum size is 50MB.';
    }

    return null;
  };
  
  const handleTextInputChange = (e) => {
    const { name, value } = e.target;
    setTextForm(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleImageInputChange = (e) => {
    const { name, files, value } = e.target;
    
    if (name === 'image' && files && files[0]) {
      const file = files[0];
      const validation = validateImageFile(file);
      
      if (validation) {
        setError(validation);
        e.target.value = '';
        return;
      }
      
      setImageForm(prev => ({
        ...prev,
        image: file
      }));
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setImageForm(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  
  const handleVideoInputChange = (e) => {
    const { name, files, value } = e.target;
    
    if (name === 'video' && files && files[0]) {
      const file = files[0];
      const validation = validateVideoFile(file);
      
      if (validation) {
        setError(validation);
        e.target.value = '';
        return;
      }
      
      setVideoForm(prev => ({
        ...prev,
        video: file
      }));
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setVideoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setVideoForm(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleAudioInputChange = (e) => {
    const { name, files, value } = e.target;

    if (name === 'audio' && files && files[0]) {
      const file = files[0];
      const validation = validateAudioFile(file);

      if (validation) {
        setError(validation);
        e.target.value = '';
        return;
      }

      setAudioForm(prev => ({
        ...prev,
        audio: file
      }));

      const reader = new FileReader();
      reader.onloadend = () => {
        setAudioPreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setAudioForm(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  
  const handleTextSubmit = async (e) => {
    e.preventDefault();
    
    if (!textForm.textContent.trim()) {
      setError('Text content is required');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      const response = await fetch('/api/specialReply/text', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(textForm)
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess('Text entry added successfully');
        loadGroups();
        setTimeout(() => {
          setShowTextModal(false);
          setTextForm({ textContent: '', categoryId: null });
          setSuccess('');
        }, 700);
      } else {
        setError(data.message || 'Failed to add text entry');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleImageSubmit = async (e) => {
    e.preventDefault();
    
    if (!imageForm.image) {
      setError('Image file is required');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      const formData = new FormData();
      formData.append('image', imageForm.image);
      formData.append('categoryId', imageForm.categoryId);
      
      const response = await fetch('/api/specialReply/image', {
        method: 'POST',
        body: formData
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess('Image uploaded successfully');
        loadGroups();
        setTimeout(() => {
          setShowImageModal(false);
          setImageForm({ categoryId: null, image: null });
          setImagePreview(null);
          setSuccess('');
        }, 700);
      } else {
        setError(data.message || 'Failed to upload image');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleVideoSubmit = async (e) => {
    e.preventDefault();
    
    if (!videoForm.video) {
      setError('Video file is required');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      const formData = new FormData();
      formData.append('video', videoForm.video);
      formData.append('categoryId', videoForm.categoryId);
      
      const response = await fetch('/api/specialReply/video', {
        method: 'POST',
        body: formData
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess('Video uploaded successfully');
        loadGroups();
        setTimeout(() => {
          setShowVideoModal(false);
          setVideoForm({ categoryId: null, video: null });
          setVideoPreview(null);
          setSuccess('');
        }, 700);
      } else {
        setError(data.message || 'Failed to upload video');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAudioSubmit = async (e) => {
    e.preventDefault();

    if (!audioForm.audio) {
      setError('Audio file is required');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const formData = new FormData();
      formData.append('audio', audioForm.audio);
      formData.append('categoryId', audioForm.categoryId);

      const response = await fetch('/api/specialReply/audio', {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Audio uploaded successfully');
        loadGroups();
        setTimeout(() => {
          setShowAudioModal(false);
          setAudioForm({ categoryId: null, audio: null });
          setAudioPreview(null);
          setSuccess('');
        }, 700);
      } else {
        setError(data.message || 'Failed to upload audio');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleEdit = (item, categoryId) => {
    setEditingItem(item);
    setEditForm({
      textContent: item.TEXT_CONTENT || '',
      mediaName: item.MEDIANAME || '',
      categoryId: categoryId,
      image: null,
      video: null
    });
    
    if (item.TYPE === 2 && item.MEDIANAME) {
      setEditImagePreview(getCurrentMediaUrl(item));
    } else if (item.TYPE === 3 && item.MEDIANAME) {
      setEditVideoPreview(getCurrentMediaUrl(item));
    } else if (item.TYPE === 4 && item.MEDIANAME) {
      setEditAudioPreview(getCurrentMediaUrl(item));
    }
    
    setShowEditModal(true);
  };
  
  const handleEditInputChange = (e) => {
    const { name, files, value } = e.target;
    
    if (name === 'image' && files && files[0]) {
      const file = files[0];
      const validation = validateImageFile(file);
      
      if (validation) {
        setError(validation);
        e.target.value = '';
        return;
      }
      
      setEditForm(prev => ({ ...prev, image: file }));
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else if (name === 'video' && files && files[0]) {
      const file = files[0];
      const validation = validateVideoFile(file);
      
      if (validation) {
        setError(validation);
        e.target.value = '';
        return;
      }
      
      setEditForm(prev => ({ ...prev, video: file }));
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditVideoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else if (name === 'audio' && files && files[0]) {
      const file = files[0];
      const validation = validateAudioFile(file);

      if (validation) {
        setError(validation);
        e.target.value = '';
        return;
      }

      setEditForm(prev => ({ ...prev, audio: file }));

      const reader = new FileReader();
      reader.onloadend = () => {
        setEditAudioPreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setEditForm(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    if (!editingItem) return;
    
    try {
      setLoading(true);
      setError('');
      
      const formData = new FormData();
      formData.append('categoryId', editForm.categoryId);
      
      if (editingItem.TYPE === 1) {
        formData.append('textContent', editForm.textContent);
      } else if (editingItem.TYPE === 2 && editForm.image) {
        formData.append('image', editForm.image);
      } else if (editingItem.TYPE === 3 && editForm.video) {
        formData.append('video', editForm.video);
      } else if (editingItem.TYPE === 4 && editForm.audio) {
        formData.append('audio', editForm.audio);
      }
      
      const response = await fetch(`/api/specialReply/${editingItem.PKKEY}`, {
        method: 'PUT',
        body: formData
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess('Entry updated successfully');
        loadGroups();
        setTimeout(() => {
          setShowEditModal(false);
          setEditingItem(null);
          setEditImagePreview(null);
          setEditVideoPreview(null);
          setEditAudioPreview(null);
          setSuccess('');
        }, 700);
      } else {
        setError(data.message || 'Failed to update entry');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleDelete = (item) => {
    setDeletingItem(item);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deletingItem) return;

    try {
      setLoading(true);
      setError('');

      const response = await fetch(`/api/specialReply/${deletingItem.PKKEY}`, {
        method: 'DELETE'
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Entry deleted successfully');
        loadGroups();
        setTimeout(() => {
          setShowDeleteModal(false);
          setDeletingItem(null);
          setSuccess('');
        }, 700);
      } else {
        setError(data.message || 'Failed to delete entry');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const closeModals = () => {
    setShowGroupModal(false);
    setShowEditGroupModal(false);
    setShowDeleteGroupModal(false);
    setShowCategoryModal(false);
    setShowEditCategoryModal(false);
    setShowDeleteCategoryModal(false);
    setShowTextModal(false);
    setShowImageModal(false);
    setShowVideoModal(false);
    setShowAudioModal(false);
    setShowEditModal(false);
    setShowDeleteModal(false);
    setAudioPreview(null);
    setEditAudioPreview(null);
    setError('');
    setSuccess('');
  };
  
  // Video loading helper
  const renderVideoWithLoading = (videoUrl, key, styles = {}) => {
    const isLoading = videoLoadingStates[key] !== false;
    
    return (
      <div className="video-wrapper" data-loading={isLoading} style={styles}>
        {isLoading && (
          <div className="video-loading-overlay">
            Loading video...
          </div>
        )}
        <video
          key={key}
          controls
          preload="metadata"
          style={styles}
          onLoadedData={() => {
            setVideoLoadingStates(prev => ({ ...prev, [key]: false }));
          }}
          onError={(e) => handleMediaError(e, key)}
        >
          <source src={videoUrl} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      </div>
    );
  };

  // ========================================
  // DRAG AND DROP HANDLERS
  // ========================================

  const handleDragStart = (e, categoryId, itemIndex, item) => {
    setDraggedItem(itemIndex);
    setDraggedCategoryId(categoryId);
    setIsDragging(true);
    setPreventFormSubmission(true);
    e.dataTransfer.effectAllowed = 'move';
    e.target.style.opacity = '0.5';
    e.dataTransfer.setData('text/plain', JSON.stringify({
      categoryId,
      itemIndex,
      itemId: item.PKKEY
    }));
    setError('');
    setDragError('');
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedItem(null);
    setDraggedCategoryId(null);
    setDragOverIndex(null);
    setIsDragging(false);
    setTimeout(() => setPreventFormSubmission(false), 300);
  };

  const handleDragOver = (e, categoryId, targetIndex) => {
    e.preventDefault();
    if (draggedCategoryId !== categoryId) {
      setDragError('Cannot drag items between different categories');
      return;
    }
    setDragError('');
    if (draggedItem !== null && draggedItem !== targetIndex) {
      setDragOverIndex(targetIndex);
    }
  };

  const handleDrop = async (e, categoryId, dropIndex, items) => {
    e.preventDefault();

    if (draggedCategoryId !== categoryId) {
      setDragError('Cannot move items between categories');
      setDraggedItem(null);
      setDraggedCategoryId(null);
      setDragOverIndex(null);
      setIsDragging(false);
      return;
    }

    if (draggedItem === null || draggedItem === dropIndex) {
      setDraggedItem(null);
      setDraggedCategoryId(null);
      setDragOverIndex(null);
      setIsDragging(false);
      return;
    }

    try {
      setIsSequenceUpdating(true);
      setError('');
      setDragError('');

      const newItems = [...items];
      const [removed] = newItems.splice(draggedItem, 1);
      newItems.splice(dropIndex, 0, removed);

      const sequences = newItems.map((item, index) => ({
        id: item.PKKEY,
        sequence: index + 1
      }));

      const response = await fetch('/api/specialReply/items/sequence/update', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sequences })
      });

      const data = await response.json();

      if (data.success) {
        await loadGroups();
        setSuccess('Sequence updated successfully');
        setTimeout(() => setSuccess(''), 2000);
      } else {
        setError(data.message || 'Failed to update sequence');
        await loadGroups();
      }
    } catch (err) {
      setError('Error: ' + err.message);
      await loadGroups();
    } finally {
      setDraggedItem(null);
      setDraggedCategoryId(null);
      setDragOverIndex(null);
      setIsDragging(false);
      setIsSequenceUpdating(false);
    }
  };

  // Render functions
  const renderItems = (items, categoryId) => {
    if (!items || items.length === 0) {
      return (
        <tr>
          <td colSpan="5" className="no-items-message">
            No items in this category. Click "Add Text", "Add Image", "Add Video", or "Add Audio" to add content.
          </td>
        </tr>
      );
    }

    return items.map((item, index) => (
      <tr
        key={item.PKKEY}
        className={`table-row draggable-row ${isDragging && draggedItem === index && draggedCategoryId === categoryId ? 'dragging' : ''} ${dragOverIndex === index && draggedCategoryId === categoryId ? 'drag-over' : ''}`}
        draggable={!loading && !isSequenceUpdating}
        onDragStart={(e) => handleDragStart(e, categoryId, index, item)}
        onDragEnd={handleDragEnd}
        onDragOver={(e) => handleDragOver(e, categoryId, index)}
        onDrop={(e) => handleDrop(e, categoryId, index, items)}
        style={{ cursor: 'grab', opacity: isDragging && draggedItem === index && draggedCategoryId === categoryId ? 0.5 : 1 }}
      >
        <td className="table-cell drag-handle">{index + 1}</td>
        <td className="table-cell">{getTypeLabel(item.TYPE)}</td>
        <td className="table-cell">
          {item.TYPE === 1 && (
            <div style={{ maxWidth: '300px', wordWrap: 'break-word' }}>
              {item.TEXT_CONTENT}
            </div>
          )}
          {item.TYPE === 2 && item.MEDIANAME && (
            <img
              src={getCurrentMediaUrl(item)}
              alt="Preview"
              onError={(e) => handleMediaError(e, item.MEDIANAME)}
              style={{
                maxWidth: '150px',
                maxHeight: '150px',
                objectFit: 'contain',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
            />
          )}
          {item.TYPE === 3 && item.MEDIANAME && (
            renderVideoWithLoading(
              getCurrentMediaUrl(item),
              item.MEDIANAME,
              { maxWidth: '200px', maxHeight: '150px' }
            )
          )}
          {item.TYPE === 4 && item.MEDIANAME && (
            <audio
              controls
              src={getCurrentMediaUrl(item)}
              className="audio-player"
              onError={(e) => handleMediaError(e, item.MEDIANAME)}
            >
              Your browser does not support the audio element.
            </audio>
          )}
        </td>
        <td className="table-cell">{item.SEQUENCE}</td>
        <td className="table-cell">
          <div className="action-buttons-inline">
            <button
              onClick={() => handleEdit(item, categoryId)}
              className="edit-button-sm"
              disabled={loading}
            >
              Edit
            </button>
            <button
              onClick={() => handleDelete(item)}
              className="delete-button-sm"
              disabled={loading}
            >
              Delete
            </button>
          </div>
        </td>
      </tr>
    ));
  };

  return (
    <Layout user={user}>
      <div className="homepage-container">
        <div className="homepage-header">
          <h1 className="homepage-title">Special Reply Management</h1>
        </div>

        {error && <div className="modal-error" style={{ marginBottom: '20px' }}>{error}</div>}
        {success && <div className="modal-success" style={{ marginBottom: '20px' }}>{success}</div>}

        {/* Add Group Button */}
        <div className="filter-section">
          <div className="action-buttons">
            <button
              onClick={handleAddGroup}
              className="new-button category-button"
              disabled={loading}
            >
              + Add Group
            </button>
          </div>
        </div>

        {/* Groups Container */}
        <div className="categories-container">
          {loading && groups.length === 0 ? (
            <div className="no-categories-message">Loading...</div>
          ) : groups.length === 0 ? (
            <div className="no-categories-message">
              No groups available. Click "Add Group" to create your first group.
            </div>
          ) : (
            groups.map(group => (
              <div key={group.PKKEY} className="category-section" style={{ marginBottom: '30px' }}>
                {/* Level 1: Group Header */}
                <div
                  className="category-header"
                  style={{ background: '#6f42c1', cursor: 'pointer' }}
                  onClick={() => toggleGroup(group.PKKEY)}
                >
                  <div className="category-info">
                    <h2 className="category-title">
                      <span className="expand-icon">
                        {expandedGroups.has(group.PKKEY) ? '▼' : '▶'}
                      </span>
                      {group.GROUP_NAME}
                    </h2>
                    <p className="category-keywords">
                      {group.categories?.length || 0} categories
                    </p>
                  </div>
                  <div className="category-actions" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => handleAddCategory(group.PKKEY)}
                      className="new-button"
                      style={{ fontSize: '14px', padding: '6px 12px' }}
                      disabled={loading}
                    >
                      + Category
                    </button>
                    <button
                      onClick={() => handleEditGroup(group)}
                      className="edit-button"
                      disabled={loading}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteGroup(group)}
                      className="delete-button"
                      disabled={loading}
                    >
                      Delete
                    </button>
                  </div>
                </div>

                {/* Level 2: Categories (inside groups) */}
                {expandedGroups.has(group.PKKEY) && (
                  <div className="category-content" style={{ padding: '20px' }}>
                    {!group.categories || group.categories.length === 0 ? (
                      <div className="no-items-message">
                        No categories in this group. Click "+ Category" to add one.
                      </div>
                    ) : (
                      group.categories.map(category => (
                        <div
                          key={category.PKKEY}
                          className="category-section"
                          style={{ marginBottom: '15px' }}
                        >
                          {/* Level 2: Category Header */}
                          <div
                            className="category-header"
                            style={{ background: '#000A30' }}
                            onClick={() => toggleCategory(group.PKKEY, category.PKKEY)}
                          >
                            <div className="category-info">
                              <h3 className="category-title" style={{ fontSize: '18px' }}>
                                <span className="expand-icon">
                                  {expandedCategories[group.PKKEY]?.has(category.PKKEY) ? '▼' : '▶'}
                                </span>
                                {category.KEYWORDS}
                              </h3>
                              <p className="category-keywords">
                                Cooldown: {category.COOLDOWN_HOURS || 24}h | 
                                Items: {category.items?.length || 0}
                                {category.STATUS === 0 && (
                                  <span className="inactive-badge">Inactive</span>
                                )}
                              </p>
                            </div>
                            <div className="category-actions" onClick={(e) => e.stopPropagation()}>
                              <button
                                onClick={() => handleAddText(category.PKKEY)}
                                className="new-button"
                                style={{ fontSize: '12px', padding: '4px 8px', marginRight: '5px' }}
                                disabled={loading}
                              >
                                + Text
                              </button>
                              <button
                                onClick={() => handleAddImage(category.PKKEY)}
                                className="new-button"
                                style={{ fontSize: '12px', padding: '4px 8px', marginRight: '5px' }}
                                disabled={loading}
                              >
                                + Image
                              </button>
                              <button
                                onClick={() => handleAddVideo(category.PKKEY)}
                                className="new-button"
                                style={{ fontSize: '12px', padding: '4px 8px', marginRight: '5px' }}
                                disabled={loading}
                              >
                                + Video
                              </button>
                              <button
                                onClick={() => handleAddAudio(category.PKKEY)}
                                className="new-button"
                                style={{ fontSize: '12px', padding: '4px 8px', marginRight: '5px' }}
                                disabled={loading}
                              >
                                + Audio
                              </button>
                              <button
                                onClick={() => handleEditCategory(category)}
                                className="edit-button"
                                style={{ fontSize: '12px', padding: '4px 8px' }}
                                disabled={loading}
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteCategory(category)}
                                className="delete-button"
                                style={{ fontSize: '12px', padding: '4px 8px' }}
                                disabled={loading}
                              >
                                Delete
                              </button>
                            </div>
                          </div>

                          {/* Level 3: Items (inside categories) */}
                          {expandedCategories[group.PKKEY]?.has(category.PKKEY) && (
                            <div className="category-content">
                              {dragError && (
                                <div style={{ color: '#dc3545', padding: '6px 12px', fontSize: '13px' }}>{dragError}</div>
                              )}
                              <div style={{ padding: '8px 12px', color: '#6c757d', fontSize: '13px', background: '#f8f9fa', borderRadius: '4px', marginBottom: '8px' }}>
                                💡 Drag and drop rows to reorder items within this category
                              </div>
                              <div className="items-table-container">
                                <table className="items-table">
                                  <thead className="table-header">
                                    <tr>
                                      <th style={{ width: '60px' }}>#</th>
                                      <th style={{ width: '100px' }}>Type</th>
                                      <th>Content</th>
                                      <th style={{ width: '100px' }}>Sequence</th>
                                      <th style={{ width: '150px' }}>Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {renderItems(category.items, category.PKKEY)}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Add Group Modal */}
        {showGroupModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h2 className="modal-title">Add New Group</h2>
              
              {error && <div className="modal-error">{error}</div>}
              {success && <div className="modal-success">{success}</div>}
              
              <form onSubmit={handleGroupSubmit}>
                <div className="form-group">
                  <label className="form-label">Group Name *</label>
                  <input
                    type="text"
                    name="groupName"
                    value={groupForm.groupName}
                    onChange={handleGroupInputChange}
                    className="form-input"
                    required
                    disabled={loading}
                    placeholder="Enter group name"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Status *</label>
                  <select
                    name="status"
                    value={groupForm.status}
                    onChange={handleGroupInputChange}
                    className="form-input"
                    disabled={loading}
                  >
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </select>
                </div>
                
                <div className="form-actions">
                  <button
                    type="button"
                    onClick={closeModals}
                    className="cancel-button"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="submit-button"
                    disabled={loading}
                  >
                    {loading ? 'Creating...' : 'Create Group'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Group Modal */}
        {showEditGroupModal && editingGroup && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h2 className="modal-title">Edit Group</h2>
              
              {error && <div className="modal-error">{error}</div>}
              {success && <div className="modal-success">{success}</div>}
              
              <form onSubmit={handleEditGroupSubmit}>
                <div className="form-group">
                  <label className="form-label">Group Name *</label>
                  <input
                    type="text"
                    name="groupName"
                    value={editGroupForm.groupName}
                    onChange={handleEditGroupInputChange}
                    className="form-input"
                    required
                    disabled={loading}
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Status *</label>
                  <select
                    name="status"
                    value={editGroupForm.status}
                    onChange={handleEditGroupInputChange}
                    className="form-input"
                    disabled={loading}
                  >
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </select>
                </div>
                
                <div className="form-actions">
                  <button
                    type="button"
                    onClick={closeModals}
                    className="cancel-button"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="submit-button"
                    disabled={loading}
                  >
                    {loading ? 'Updating...' : 'Update Group'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Group Modal */}
        {showDeleteGroupModal && deletingGroup && (
          <div className="modal-overlay">
            <div className="modal-content" style={{ maxWidth: '400px' }}>
              <h2 className="modal-title">Confirm Delete</h2>
              
              {error && <div className="modal-error">{error}</div>}
              {success && <div className="modal-success">{success}</div>}
              
              <div style={{ marginBottom: '20px' }}>
                <p>Are you sure you want to delete the group "{deletingGroup.GROUP_NAME}"?</p>
                <p style={{ color: '#dc3545', fontSize: '14px' }}>
                  Note: You can only delete groups that have no categories.
                </p>
              </div>
              
              <div className="form-actions">
                <button
                  type="button"
                  onClick={closeModals}
                  className="cancel-button"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteGroup}
                  className="submit-button"
                  disabled={loading}
                  style={{
                    backgroundColor: '#dc3545',
                    borderColor: '#dc3545'
                  }}
                >
                  {loading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Category Modal */}
        {showCategoryModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h2 className="modal-title">Add New Category</h2>
              
              {error && <div className="modal-error">{error}</div>}
              {success && <div className="modal-success">{success}</div>}
              
              <form onSubmit={handleCategorySubmit}>
                <div className="form-group">
                  <label className="form-label">Keywords *</label>
                  <input
                    type="text"
                    name="keywords"
                    value={categoryForm.keywords}
                    onChange={handleCategoryInputChange}
                    className="form-input"
                    required
                    disabled={loading}
                    placeholder="Enter keywords"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Group *</label>
                  <select
                    name="groupId"
                    value={categoryForm.groupId || ''}
                    onChange={handleCategoryInputChange}
                    className="form-input"
                    required
                    disabled={loading}
                  >
                    <option value="">Select a group</option>
                    {groups.map(group => (
                      <option key={group.PKKEY} value={group.PKKEY}>
                        {group.GROUP_NAME}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label className="form-label">Cooldown Hours *</label>
                  <input
                    type="number"
                    name="cooldownHours"
                    value={categoryForm.cooldownHours}
                    onChange={handleCategoryInputChange}
                    className="form-input"
                    min="0"
                    required
                    disabled={loading}
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Status *</label>
                  <select
                    name="status"
                    value={categoryForm.status}
                    onChange={handleCategoryInputChange}
                    className="form-input"
                    disabled={loading}
                  >
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </select>
                </div>
                
                <div className="form-actions">
                  <button
                    type="button"
                    onClick={closeModals}
                    className="cancel-button"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="submit-button"
                    disabled={loading}
                  >
                    {loading ? 'Creating...' : 'Create Category'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Category Modal */}
        {showEditCategoryModal && editingCategory && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h2 className="modal-title">Edit Category</h2>
              
              {error && <div className="modal-error">{error}</div>}
              {success && <div className="modal-success">{success}</div>}
              
              <form onSubmit={handleEditCategorySubmit}>
                <div className="form-group">
                  <label className="form-label">Keywords *</label>
                  <input
                    type="text"
                    name="keywords"
                    value={editCategoryForm.keywords}
                    onChange={handleEditCategoryInputChange}
                    className="form-input"
                    required
                    disabled={loading}
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Group *</label>
                  <select
                    name="groupId"
                    value={String(editCategoryForm.groupId || '')}
                    onChange={handleEditCategoryInputChange}
                    className="form-input"
                    required
                    disabled={loading}
                  >
                    <option value="">Select a group</option>
                    {groups.map(group => (
                      <option key={group.PKKEY} value={String(group.PKKEY)}>
                        {group.GROUP_NAME}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label className="form-label">Cooldown Hours *</label>
                  <input
                    type="number"
                    name="cooldownHours"
                    value={editCategoryForm.cooldownHours}
                    onChange={handleEditCategoryInputChange}
                    className="form-input"
                    min="0"
                    required
                    disabled={loading}
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Status *</label>
                  <select
                    name="status"
                    value={editCategoryForm.status}
                    onChange={handleEditCategoryInputChange}
                    className="form-input"
                    disabled={loading}
                  >
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </select>
                </div>
                
                <div className="form-actions">
                  <button
                    type="button"
                    onClick={closeModals}
                    className="cancel-button"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="submit-button"
                    disabled={loading}
                  >
                    {loading ? 'Updating...' : 'Update Category'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Category Modal */}
        {showDeleteCategoryModal && deletingCategory && (
          <div className="modal-overlay">
            <div className="modal-content" style={{ maxWidth: '400px' }}>
              <h2 className="modal-title">Confirm Delete</h2>
              
              {error && <div className="modal-error">{error}</div>}
              {success && <div className="modal-success">{success}</div>}
              
              <div style={{ marginBottom: '20px' }}>
                <p>Are you sure you want to delete the category "{deletingCategory.KEYWORDS}"?</p>
                <p style={{ color: '#dc3545', fontSize: '14px' }}>
                  Note: You can only delete categories that have no items.
                </p>
              </div>
              
              <div className="form-actions">
                <button
                  type="button"
                  onClick={closeModals}
                  className="cancel-button"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteCategory}
                  className="submit-button"
                  disabled={loading}
                  style={{
                    backgroundColor: '#dc3545',
                    borderColor: '#dc3545'
                  }}
                >
                  {loading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Text Modal */}
        {showTextModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h2 className="modal-title">Add Text Entry</h2>
              
              {error && <div className="modal-error">{error}</div>}
              {success && <div className="modal-success">{success}</div>}
              
              <form onSubmit={handleTextSubmit}>
                <div className="form-group">
                  <label className="form-label">Text Content *</label>
                  <textarea
                    name="textContent"
                    value={textForm.textContent}
                    onChange={handleTextInputChange}
                    className="form-textarea"
                    rows="5"
                    required
                    disabled={loading}
                  />
                </div>
                
                <div className="form-actions">
                  <button
                    type="button"
                    onClick={closeModals}
                    className="cancel-button"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="submit-button"
                    disabled={loading}
                  >
                    {loading ? 'Adding...' : 'Add Text'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Add Image Modal */}
        {showImageModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h2 className="modal-title">Add Image Entry</h2>
              
              {error && <div className="modal-error">{error}</div>}
              {success && <div className="modal-success">{success}</div>}
              
              <form onSubmit={handleImageSubmit}>
                <div className="form-group">
                  <label className="form-label">Image File *</label>
                  <input
                    type="file"
                    name="image"
                    accept="image/jpeg,image/jpg,image/png,image/gif"
                    onChange={handleImageInputChange}
                    className="form-input"
                    required
                    disabled={loading}
                  />
                  <small className="form-hint">
                    Accepted formats: JPG, JPEG, PNG, GIF (Max: 50MB)
                  </small>
                  
                  {imagePreview && (
                    <div className="preview-container" style={{ marginTop: '10px' }}>
                      <label className="form-label">Preview:</label>
                      <img 
                        src={imagePreview} 
                        alt="Preview" 
                        style={{ 
                          maxWidth: '200px', 
                          maxHeight: '200px', 
                          objectFit: 'contain',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          display: 'block'
                        }} 
                      />
                    </div>
                  )}
                </div>
                
                <div className="form-actions">
                  <button
                    type="button"
                    onClick={closeModals}
                    className="cancel-button"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="submit-button"
                    disabled={loading}
                  >
                    {loading ? 'Uploading...' : 'Add Image'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Add Video Modal */}
        {showVideoModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h2 className="modal-title">Add Video Entry</h2>
              
              {error && <div className="modal-error">{error}</div>}
              {success && <div className="modal-success">{success}</div>}
              
              <form onSubmit={handleVideoSubmit}>
                <div className="form-group">
                  <label className="form-label">Video File *</label>
                  <input
                    type="file"
                    name="video"
                    accept="video/mp4"
                    onChange={handleVideoInputChange}
                    className="form-input"
                    required
                    disabled={loading}
                  />
                  <small className="form-hint">
                    Accepted formats: MP4 only (Max: 50MB)
                  </small>
                  
                  {videoPreview && (
                    <div className="preview-container" style={{ marginTop: '10px' }}>
                      <label className="form-label">Preview:</label>
                      {renderVideoWithLoading(videoPreview, 'new-video-preview', { maxWidth: '300px', maxHeight: '200px' })}
                    </div>
                  )}
                </div>
                
                <div className="form-actions">
                  <button
                    type="button"
                    onClick={closeModals}
                    className="cancel-button"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="submit-button"
                    disabled={loading}
                  >
                    {loading ? 'Uploading...' : 'Add Video'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Add Audio Modal */}
        {showAudioModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h2 className="modal-title">Add Audio Entry</h2>

              {error && <div className="modal-error">{error}</div>}
              {success && <div className="modal-success">{success}</div>}

              <form onSubmit={handleAudioSubmit}>
                <div className="form-group">
                  <label className="form-label">Audio File *</label>
                  <input
                    type="file"
                    name="audio"
                    accept="audio/mpeg,audio/mp3,audio/ogg,audio/wav,audio/aac,audio/x-m4a"
                    onChange={handleAudioInputChange}
                    className="form-input"
                    required
                    disabled={loading}
                  />
                  <small className="form-hint">
                    Accepted formats: MP3, OGG, WAV, AAC, M4A (Max: 50MB)
                  </small>

                  {audioPreview && (
                    <div className="preview-container" style={{ marginTop: '10px' }}>
                      <label className="form-label">Preview:</label>
                      <audio
                        controls
                        src={audioPreview}
                        className="audio-player"
                        style={{ width: '100%', marginTop: '6px' }}
                      >
                        Your browser does not support the audio element.
                      </audio>
                    </div>
                  )}
                </div>

                <div className="form-actions">
                  <button
                    type="button"
                    onClick={closeModals}
                    className="cancel-button"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="submit-button"
                    disabled={loading}
                  >
                    {loading ? 'Uploading...' : 'Add Audio'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {showEditModal && editingItem && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h2 className="modal-title">Edit {getTypeLabel(editingItem.TYPE)} Entry</h2>
              
              {error && <div className="modal-error">{error}</div>}
              {success && <div className="modal-success">{success}</div>}
              
              <form onSubmit={handleEditSubmit}>
                {editingItem.TYPE === 1 && (
                  <div className="form-group">
                    <label className="form-label">Text Content *</label>
                    <textarea
                      name="textContent"
                      value={editForm.textContent}
                      onChange={handleEditInputChange}
                      className="form-textarea"
                      rows="5"
                      required
                      disabled={loading}
                    />
                  </div>
                )}
                
                {editingItem.TYPE === 2 && (
                  <div className="form-group">
                    <label className="form-label">Replace Image (Optional)</label>
                    <input
                      type="file"
                      name="image"
                      accept="image/jpeg,image/jpg,image/png,image/gif"
                      onChange={handleEditInputChange}
                      className="form-input"
                      disabled={loading}
                    />
                    <small className="form-hint">
                      Leave empty to keep current image
                    </small>
                    
                    {editImagePreview && (
                      <div className="preview-container" style={{ marginTop: '10px' }}>
                        <label className="form-label">Current/Preview Image:</label>
                        <img 
                          src={editImagePreview} 
                          alt="Preview" 
                          onError={(e) => handleMediaError(e, editingItem.MEDIANAME)}
                          style={{ 
                            maxWidth: '200px', 
                            maxHeight: '200px', 
                            objectFit: 'contain',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            display: 'block'
                          }} 
                        />
                      </div>
                    )}
                  </div>
                )}
                
                {editingItem.TYPE === 3 && (
                  <div className="form-group">
                    <label className="form-label">Replace Video (Optional)</label>
                    <input
                      type="file"
                      name="video"
                      accept="video/mp4"
                      onChange={handleEditInputChange}
                      className="form-input"
                      disabled={loading}
                    />
                    <small className="form-hint">
                      Leave empty to keep current video
                    </small>
                    
                    {editVideoPreview && (
                      <div className="preview-container" style={{ marginTop: '10px' }}>
                        <label className="form-label">Current/Preview Video:</label>
                        {renderVideoWithLoading(editVideoPreview, editingItem.MEDIANAME || 'edit-preview', { maxWidth: '300px', maxHeight: '200px' })}
                      </div>
                    )}
                  </div>
                )}

                {editingItem.TYPE === 4 && (
                  <div className="form-group">
                    <label className="form-label">Replace Audio (Optional)</label>
                    <input
                      type="file"
                      name="audio"
                      accept="audio/mpeg,audio/mp3,audio/ogg,audio/wav,audio/aac,audio/x-m4a"
                      onChange={handleEditInputChange}
                      className="form-input"
                      disabled={loading}
                    />
                    <small className="form-hint">
                      Leave empty to keep current audio
                    </small>

                    {editAudioPreview && (
                      <div className="preview-container" style={{ marginTop: '10px' }}>
                        <label className="form-label">Current/Preview Audio:</label>
                        <audio
                          controls
                          src={editAudioPreview}
                          className="audio-player"
                          style={{ width: '100%', marginTop: '6px' }}
                        >
                          Your browser does not support the audio element.
                        </audio>
                      </div>
                    )}
                  </div>
                )}
                
                <div className="form-actions">
                  <button
                    type="button"
                    onClick={closeModals}
                    className="cancel-button"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="submit-button"
                    disabled={loading}
                  >
                    {loading ? 'Updating...' : 'Update'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Item Modal */}
        {showDeleteModal && deletingItem && (
          <div className="modal-overlay">
            <div className="modal-content" style={{ maxWidth: '400px' }}>
              <h2 className="modal-title">Confirm Delete</h2>
              
              {error && <div className="modal-error">{error}</div>}
              {success && <div className="modal-success">{success}</div>}
              
              <div style={{ marginBottom: '20px' }}>
                <p>Are you sure you want to delete this {getTypeLabel(deletingItem.TYPE).toLowerCase()} entry?</p>
              </div>
              
              <div className="form-actions">
                <button
                  type="button"
                  onClick={closeModals}
                  className="cancel-button"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="submit-button"
                  disabled={loading}
                  style={{
                    backgroundColor: '#dc3545',
                    borderColor: '#dc3545'
                  }}
                >
                  {loading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default SpecialReplyPage;